import * as React from 'react';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { IExtensionsSettingsCtrlScope } from '../../../settings/controllers/SiteSettingsForExtensionsCtrl';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import {
  IExtensionsSiteSettings,
  ISiteSettingsForSiteAdmin
} from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ServerSettings, ServerSettingsInstance } from '../../../settings/services/ServerSettings';
import { Sites } from '../../../ts/resources/Sites';
import { IGetExtensionsSiteSettingsResult } from '../../../ts/ServerApi/requests/GetExtensionsSiteSettingsRequest';
import { IGetExtsvcServerSettingsResult } from '../../../ts/ServerApi/requests/GetExtsvcServerSettingsRequest';
import { IExtensionsServerSettings, IExtsvcServerSettings } from '../../../ts/ServerApi/types/IEditableServerSettings';
import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';


const template = `<div ng-include="'siteSettingsShouldShowExtensions.html'"></div>`;

export interface ExtensionsSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly angularContext: AngularContext;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface ExtensionsSiteSettingsSubspaceAngularProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
}

export type ExtensionsSiteSettingsSubspaceProps = ExtensionsSiteSettingsSubspaceOwnProps & ExtensionsSiteSettingsSubspaceAngularProps;

export interface ExtensionsSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly extensionServerSettings: IExtensionsServerSettings;
  readonly siteSettings: IGetExtensionsSiteSettingsResult;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly extsvcServerSettings: IExtsvcServerSettings;
}

export class ExtensionsSiteSettingsSubspaceComponent extends React.PureComponent<ExtensionsSiteSettingsSubspaceProps, ExtensionsSiteSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope<IExtensionsSiteSettings> = undefined;
  private unmounted: boolean = false;

  private initialState: ExtensionsSiteSettingsSubspaceState = {hasDataToRender: false, hasError: false, extensionServerSettings: undefined, siteSettings: undefined, siteSettingsForServerAdmin: undefined, extsvcServerSettings: undefined};

  public constructor(props: ExtensionsSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: ExtensionsSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.extensionServerSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      if (this.props.urls instanceof SpacesSiteUrls) {
        return <RouterError errorType={'default'} urls={this.props.urls} />;
      } else {
        return <RouterError errorType={'server-default'} urls={this.props.urls} />;
      }
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={'tb-stylish-settings'}>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private updateState<K extends keyof ExtensionsSiteSettingsSubspaceState>(newState: Pick<ExtensionsSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getExtensionsServerSettings();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
    if(FeatureFlags.extSvcSiteLevelSettingsEnabled) {
      this.getExtsvcServerSettings();
    }
  }

  private getExtensionsServerSettings = async () => {
    try {
      const extensionServerSettings = await this.props.serverSettingsService.getExtensionsSettings();
      this.updateState({extensionServerSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getExtsvcServerSettings = async () => {
    try {
      const extsvcServerSettings = await this.props.serverSettingsService.getExtsvcSettings();
      this.updateState({extsvcServerSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentExtensionsSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForServerAdmin = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const serverSettings = await this.props.sites.getCurrentSiteSettingsForServerAdmin();
      this.updateState({siteSettingsForServerAdmin: serverSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISettingsCoordinatorCtrlScope<IExtensionsSiteSettings>;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;
    childScope.extensionsServerSettings = this.state.extensionServerSettings;
    childScope.extsvcServerSettings = this.state.extsvcServerSettings;
    childScope.siteSettings = this.state.siteSettings;
    childScope.siteSettingsForServerAdmin = this.state.siteSettingsForServerAdmin;
    this.childScope = childScope;
    return childScope;
  }
}

const ExtensionsSiteSettingsHOC = angularProviderHOC<ExtensionsSiteSettingsSubspaceOwnProps, ExtensionsSiteSettingsSubspaceAngularProps, ExtensionsSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    serverSettingsService: ServerSettingsInstance,
    sites: ServiceLocator.get<Sites>('Sites'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const ExtensionsSiteSettingsSubspace: React.ComponentType<ExtensionsSiteSettingsSubspaceOwnProps>
  = ExtensionsSiteSettingsHOC(ExtensionsSiteSettingsSubspaceComponent);
